import React, { useState } from 'react';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';
import { closeDialog } from '@/features/dialog/dialogSlice';
import { SCOPES } from '@/features/config/scopes';
import { ShareField } from '@/components/dialogs/ShareDialog/ShareField';
import { AdminLedgerPicker } from '../shared/pickers/AdminLedgerPicker';
import { AdminBrokerGroupPicker } from '../shared/pickers/AdminBrokerGroupPicker';
import { useAdminConfirm } from '../shared/AdminConfirmProvider';
import { useShareFormFieldConfigsMutation } from './api/formFieldConfigsApi';

export const SHARE_FORM_FIELD_CONFIGS_DIALOG = 'shareFormFieldConfigs';

/**
 * Shares the ticked form field configs with any ledgers and broker groups, in any ledger,
 * after the admin confirms. Each target's config for those fields is replaced with a copy;
 * later edits don't follow. Opened with openDialog({ dialogName:
 * SHARE_FORM_FIELD_CONFIGS_DIALOG, data: { docIds } }); mount it only while open, so each
 * opening starts empty.
 */
export const ShareFormFieldConfigsDialog = () => {
	const dispatch = useDispatch();
	const confirm = useAdminConfirm();
	const { docIds } = useSelector((state) => state.dialog.data[SHARE_FORM_FIELD_CONFIGS_DIALOG]);
	const onClose = () => dispatch(closeDialog(SHARE_FORM_FIELD_CONFIGS_DIALOG));
	const [ledgers, setLedgers] = useState([]);
	const [brokerGroupIds, setBrokerGroupIds] = useState([]);
	const [shareFormFieldConfigs, { isLoading }] = useShareFormFieldConfigsMutation();

	const targets = [
		...ledgers.map((scopeId) => ({ scope: SCOPES.LEDGER, scopeId })),
		...brokerGroupIds.map((scopeId) => ({ scope: SCOPES.BROKER_GROUP, scopeId })),
	];

	const handleShare = async () => {
		const confirmed = await confirm({
			title: `Share ${docIds.length} field${docIds.length === 1 ? '' : 's'}?`,
			content:
				"Each chosen ledger's and broker group's config for these fields is replaced with a copy of this one. A broker group's own config still wins over its ledger's.",
			confirmText: 'Share',
		});
		if (!confirmed) return;

		const result = await shareFormFieldConfigs({ docIds, targets });
		if (result.error) {
			dispatch(
				showToast({ message: "Couldn't share the config. Please try again.", error: true }),
			);
			return;
		}
		dispatch(
			showToast({
				message: 'Shared the config',
				success: true,
				duration: 4000,
				autohide: true,
			}),
		);
		onClose();
	};

	return (
		<Dialog
			open
			onClose={onClose}
			slotProps={{ paper: { sx: { minWidth: { xs: '100%', sm: '70%', md: '50%' } } } }}
		>
			<DialogTitle>{`Share ${docIds.length} config setting${docIds.length === 1 ? '' : 's'}`}</DialogTitle>
			<DialogContent sx={{ paddingBottom: '2em' }}>
				<Stack spacing={2}>
					<Typography variant='body2'>
						Copies these settings into each chosen ledger and broker group, replacing
						their config for these fields.
					</Typography>
					<ShareField label='Ledgers'>
						<AdminLedgerPicker value={ledgers} onChange={setLedgers} showCheckbox />
					</ShareField>
					<ShareField label='Broker groups'>
						<AdminBrokerGroupPicker
							value={brokerGroupIds}
							onChange={setBrokerGroupIds}
							showCheckbox
						/>
					</ShareField>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
				<Button
					variant='contained'
					onClick={handleShare}
					disabled={!targets.length || isLoading}
				>
					Share
				</Button>
			</DialogActions>
		</Dialog>
	);
};

ShareFormFieldConfigsDialog.displayName = 'ShareFormFieldConfigsDialog';
